
/**
 * Utilities for handling image optimizations and transformations
 */

/**
 * Interface for tracking image error states
 */
export interface ImageErrorState {
  retryCount: number;
  hasError: boolean;
}

/**
 * Returns an optimized URL for an image with the specified width
 * 
 * @param url Original image URL
 * @param width Desired width in pixels
 * @returns Optimized image URL
 */
export const getOptimizedImageUrl = (url: string | null, width = 800): string | null => {
  if (!url) return null;
  
  // Skip optimization for videos, SVGs, and GIFs
  if (url.includes('.mp4') || url.includes('.webm') || 
      url.includes('.svg') || url.includes('.gif')) {
    return url;
  }
  
  // Handle URLs that already have query parameters
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}width=${width}&quality=80`;
};

/**
 * Generate a thumbnail URL from a full-size image URL
 * 
 * @param url Original image URL
 * @param width Thumbnail width in pixels
 * @returns Thumbnail URL
 */
export const getThumbnailUrl = (url: string | null, width = 100): string | null => {
  if (!url) return null;
  
  // Skip thumbnails for videos, SVGs, and GIFs
  if (url.includes('.mp4') || url.includes('.webm') || 
      url.includes('.svg') || url.includes('.gif')) {
    return url;
  }
  
  // Handle URLs that already have query parameters
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}width=${width}&quality=60`;
};

/**
 * Detects the aspect ratio of an image URL
 * Returns a promise that resolves to an object with width and height
 * 
 * @param url Image URL
 * @returns Promise with image dimensions
 */
export const getImageAspectRatio = (url: string): Promise<{width: number, height: number}> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };
    img.onerror = () => {
      reject(new Error(`Failed to load image: ${url}`));
    };
    img.src = url;
  });
};

/**
 * Handles image loading errors with retry logic
 * 
 * @param event Error event from img element
 * @param setErrorState State setter for error tracking
 * @param errorState Current error state
 * @param url Image URL that failed
 */
export const handleImageError = (
  event: React.SyntheticEvent<HTMLImageElement>,
  setErrorState: React.Dispatch<React.SetStateAction<ImageErrorState>>,
  errorState: ImageErrorState,
  url: string | null
): void => {
  // Log the error details
  console.error('Image failed to load:', { url, retryCount: errorState.retryCount });
  
  // Implement retry logic with a maximum of 2 retries
  if (errorState.retryCount < 2) {
    // Increment retry count and reset error
    setErrorState({
      retryCount: errorState.retryCount + 1,
      hasError: false
    });
    
    // Add cache-busting parameter for retry
    if (event.currentTarget && url) {
      const cacheBuster = `cache=${Date.now()}`;
      const separator = url.includes('?') ? '&' : '?';
      event.currentTarget.src = `${url}${separator}${cacheBuster}`;
    }
  } else {
    // Max retries reached, mark as permanent error
    setErrorState({
      retryCount: errorState.retryCount,
      hasError: true
    });
  }
};

/**
 * Prepares an image for upload, handling validation and preprocessing
 * 
 * @param file The image file to upload
 * @param bucketName The storage bucket to upload to
 * @returns Upload result with path and other metadata
 */
export const prepareImageUpload = async (
  file: File, 
  bucketName: string
): Promise<{ path: string; contentType: string }> => {
  // Validate file is an image
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }
  
  // Generate a unique filename
  const fileExt = file.name.split('.').pop();
  const fileName = `${Math.random().toString(36).substring(2, 15)}-${Date.now()}.${fileExt}`;
  const filePath = `uploads/${fileName}`;
  
  // Return path info for the upload
  return {
    path: filePath,
    contentType: file.type
  };
};
